import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";

/**
 * Mapa de slug de compañía en la URL -> nombre de compañía
 * /cia/kf83mx => Primera Compañía, etc.
 */
const CIA_MAP = {
  kf83mx: "Primera Compañía",
  zl29qp: "Segunda Compañía",
  rd77lt: "Tercera Compañía",
  vb52cz: "Cuarta Compañía",
  wn64dy: "Quinta Compañía",
};

/** Obtiene la compañía actual leyendo la URL (/cia/<slug>). */
function getCompaniaActualFromLocation() {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname || "";
  const match = path.match(/\/cia\/([^/]+)/);
  const slug = match ? match[1] : null;
  if (!slug) return null;
  const nombre = CIA_MAP[slug];
  if (!nombre) return null;
  return { slug, nombre };
}

/**
 * Normaliza nombre de compañía para comparar sin tildes / mayúsculas / espacios.
 */
function normCompaniaName(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // quitar tildes
    .replace(/\s+/g, "");
}

function sameCompaniaName(a, b) {
  if (!a || !b) return false;
  return normCompaniaName(a) === normCompaniaName(b);
}

/**
 * Normaliza el RUT para lookup.
 */
function normalizeRut(raw) {
  if (!raw) return { raw: "", compact: "", withDash: "", withDotDash: "" };
  const s = String(raw).trim().replace(/\./g, "").replace(/\s+/g, "");
  const sinGuion = s.replace(/-/g, "");
  if (sinGuion.length < 2) {
    return { raw: s, compact: sinGuion.toUpperCase(), withDash: s, withDotDash: s };
  }
  const cuerpo = sinGuion.slice(0, -1);
  const dv = sinGuion.slice(-1).toUpperCase();
  const compact = `${cuerpo}${dv}`;
  const withDash = `${cuerpo}-${dv}`;
  return { raw: s, compact, withDash, withDotDash: withDash };
}

// Acepta tanto Timestamp como string/Date y devuelve Date o null
function toDateFlexible(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  if (v?.toDate) return v.toDate();
  if (typeof v === "string") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// Convierte input type="datetime-local" -> Date en local
function fromLocalInput(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function normRut(s) {
  return String(s || "")
    .replace(/\./g, "")
    .replace(/-/g, "")
    .trim()
    .toUpperCase();
}
function sameRut(a, b) {
  if (!a || !b) return false;
  return normRut(a) === normRut(b);
}

export default function Bomberos81Modal({ open, onClose }) {
  const { cuerpoId } = useCuerpo();
  const [rut, setRut] = useState("");
  const [desde, setDesde] = useState(""); // datetime-local
  const [hasta, setHasta] = useState(""); // datetime-local
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [motivo, setMotivo] = useState("");
  const [activos, setActivos] = useState([]);

  // compañía actual según URL (/cia/xxxx)
  const companiaActual = useMemo(() => getCompaniaActualFromLocation(), []);

  // Escucha la colección de fuera de servicio vigente
  useEffect(() => {
    if (!open || !cuerpoId) return;
    const colRef = collection(db, "bomberos_fuera_servicio");
    const qRef = query(
      colRef,
      where("cuerpo_id", "==", cuerpoId)
    );
    const unsub = onSnapshot(qRef, (snap) => {
      const now = new Date();
      const vivos = [];
      snap.forEach((d) => {
        const x = d.data();
        const ini = toDateFlexible(x.fecha_inicio);
        const fin = toDateFlexible(x.fecha_fin);
        if (!ini || !fin) return;
        if (ini <= now && now <= fin) {
          vivos.push({ id: d.id, ...x });
        } else if (now > fin) {
          // limpieza suave: borrar vencidos
          deleteDoc(doc(db, "bomberos_fuera_servicio", d.id)).catch(() => {});
        }
      });

      // 🔹 FILTRO POR COMPAÑÍA ACTUAL
      const filtrados =
        companiaActual
          ? vivos.filter((b) =>
              sameCompaniaName(
                b.compania || b.compañía || b.companiaNombre || "",
                companiaActual.nombre
              )
            )
          : vivos;

      setActivos(filtrados);
    });
    return () => unsub();
  }, [open, companiaActual, cuerpoId]);

  // Agrupa por compañía (aunque ya vienen filtrados)
  const grouped = useMemo(() => {
    const g = {};
    for (const b of activos) {
      const cia = b.compania || b.compañía || "Sin Compañía";
      if (!g[cia]) g[cia] = [];
      g[cia].push(b);
    }
    return g;
  }, [activos]);

  // Busca el bombero en "bomberos"
  async function fetchBomberoByRut(rutInput) {
    const n = normalizeRut(rutInput);
    // 1) Intentar por docId exactos
    const ids = [n.withDash, n.compact];
    for (const id of ids) {
      const ref = doc(db, "bomberos", id);
      const snap = await getDoc(ref);
      if (snap.exists() && snap.data()?.cuerpo_id === cuerpoId) {
        return { id, data: snap.data() };
      }
    }
    // 2) Intentar por campo "rut"
    const candidates = Array.from(
      new Set([n.withDash, n.compact, n.withDash.toLowerCase(), n.compact.toLowerCase()])
    ).slice(0, 10);
    try {
      const q1 = query(
        collection(db, "bomberos"),
        where("cuerpo_id", "==", cuerpoId)
      );
      const s1 = await getDocs(q1);
      const d = s1.docs.find((docSnap) => {
        const rutDoc = normalizeRut(docSnap.data()?.rut || docSnap.id);
        return candidates.some((c) => normRut(c) === rutDoc.compact);
      });
      if (d) {
        return { id: d.id, data: d.data() };
      }
    } catch (e) {
      // índice/campo faltante: ignorar
    }
    return null;
  }

  async function handleIngresar() {
    setErrorMsg("");
    const n = normalizeRut(rut);
    if (!n.compact) {
      setErrorMsg("Ingresa un RUT válido.");
      return;
    }
    const d1 = fromLocalInput(desde);
    const d2 = fromLocalInput(hasta);
    if (!d1 || !d2) {
      setErrorMsg("Selecciona fecha y hora DESDE y HASTA.");
      return;
    }
    if (d1 > d2) {
      setErrorMsg("La fecha/hora DESDE no puede ser mayor que HASTA.");
      return;
    }
    if (!motivo.trim()) {
      setErrorMsg("Debes ingresar un motivo.");
      return;
    }

    setLoading(true);
    try {
      const found = await fetchBomberoByRut(n.withDash);
      if (!found) {
        setErrorMsg(
          "RUT no registrado en la base de datos de bomberos (se acepta con o sin guión)."
        );
        setLoading(false);
        return;
      }
      const info = found.data || {};
      const nombre =
        info.nombreCompleto ||
        info.nombre ||
        `${info.nombres || ""} ${info.apellidos || ""}`.trim() ||
        "Sin nombre";
      const companiaDoc = info.compania || info.compañía || "Sin Compañía";
      const color = info.color || info.colorCompania || null;
      const cargo = info.cargo || null;
      const fotoURL = info.fotoURL || null;

      // 🔹 VALIDAR QUE EL BOMBERO PERTENECE A ESTA COMPAÑÍA (SEGÚN URL)
      if (companiaActual && !sameCompaniaName(companiaDoc, companiaActual.nombre)) {
        setErrorMsg(
          `Este bombero pertenece a "${companiaDoc}", pero esta es la plataforma de "${companiaActual.nombre}".`
        );
        setLoading(false);
        return;
      }

      // Usar ID estable sin formato para evitar duplicados (compacto)
      const docId = n.compact;
      await setDoc(
        doc(db, "bomberos_fuera_servicio", docId),
        {
          cuerpo_id: cuerpoId,
          rut: n.withDash, // legible con guión
          rut_compacto: n.compact,
          nombre,
          compania: companiaDoc,
          color,
          cargo,
          fotoURL,
          fecha_inicio: Timestamp.fromDate(d1),
          fecha_fin: Timestamp.fromDate(d2),
          motivo: motivo.trim(),
          active: true,
          visible: true,
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        },
        { merge: true }
      );

      // limpiar formulario
      setRut("");
      setDesde("");
      setHasta("");
      setMotivo("");
    } catch (e) {
      console.error("Registrar 0-8 error:", e);
      if (e?.code === "permission-denied") {
        setErrorMsg("Permisos insuficientes para guardar. Revisa las reglas de Firestore.");
      } else {
        setErrorMsg("No se pudo registrar el periodo 0-8. Intenta nuevamente.");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleSalirConfirm(b) {
    const rutIngresado = window.prompt(
      "Confirma tu RUT para salir (puede ser con o sin puntos/guión):"
    );
    if (!rutIngresado) return;
    const coincide =
      sameRut(rutIngresado, b.rut) ||
      sameRut(rutIngresado, b.rut_compacto) ||
      sameRut(rutIngresado, b.id);
    if (!coincide) {
      alert("El RUT no coincide. Inténtalo nuevamente.");
      return;
    }
    try {
      await deleteDoc(doc(db, "bomberos_fuera_servicio", b.id));
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar el registro. Reintenta.");
    }
  }

  if (!open) return null;

  return createPortal(
    <div
      className="b81-backdrop"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 4000,
      }}
    >
      <div
        className="b81-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(960px, 95vw)",
          maxHeight: "90vh",
          overflow: "auto",
          background: "#121820",
          color: "#fff",
          borderRadius: 14,
          boxShadow: "0 10px 40px rgba(0,0,0,.5)",
          padding: "16px 18px 20px",
        }}
      >
        <div
          className="b81-header"
          style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          <h2 style={{ margin: 0 }}>Bomberos 0-8 (fuera de servicio)</h2>
          <button
            className="b81-close"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "none",
              color: "#fff",
              fontSize: 22,
              cursor: "pointer",
            }}
          >
            ✕
          </button>
        </div>

        {/* Formulario */}
        <style>{`@media (max-width: 820px){ .b81-form{ grid-template-columns: 1fr !important; } .b81-btn{ align-self: stretch !important; } }`}</style>
        <div
          className="b81-form"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr auto",
            gap: 12,
            margin: "12px 0 6px",
          }}
        >
          <div className="b81-field" style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: ".85rem", opacity: 0.9, marginBottom: 4 }}>RUT</label>
            <input
              type="text"
              placeholder="12.345.678-9"
              value={rut}
              onChange={(e) => setRut(e.target.value)}
              style={{
                background: "#0e1319",
                border: "1px solid #2a3442",
                color: "#fff",
                borderRadius: 8,
                padding: 10,
              }}
            />
          </div>

          <div className="b81-field" style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: ".85rem", opacity: 0.9, marginBottom: 4 }}>Desde</label>
            <input
              type="datetime-local"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              step="60"
              lang="es-CL"
              style={{
                background: "#0e1319",
                border: "1px solid #2a3442",
                color: "#fff",
                borderRadius: 8,
                padding: 10,
              }}
            />
          </div>

          <div className="b81-field" style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: ".85rem", opacity: 0.9, marginBottom: 4 }}>Hasta</label>
            <input
              type="datetime-local"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              step="60"
              lang="es-CL"
              style={{
                background: "#0e1319",
                border: "1px solid #2a3442",
                color: "#fff",
                borderRadius: 8,
                padding: 10,
              }}
            />
          </div>

          <div className="b81-field" style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: ".85rem", opacity: 0.9, marginBottom: 4 }}>Motivo</label>
            <input
              type="text"
              placeholder="Ej: Permiso médico, turno laboral, viaje..."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              required
              style={{
                background: "#0e1319",
                border: "1px solid #2a3442",
                color: "#fff",
                borderRadius: 8,
                padding: 10,
              }}
            />
          </div>

          <button
            className="b81-btn"
            disabled={loading}
            onClick={handleIngresar}
            style={{
              background: "#1e88e5",
              border: "none",
              borderRadius: 10,
              color: "#fff",
              padding: "10px 14px",
              cursor: loading ? "not-allowed" : "pointer",
              alignSelf: "end",
              height: 42,
            }}
          >
            {loading ? "Guardando..." : "Registrar 0-8"}
          </button>

          {errorMsg && (
            <div
              className="b81-error"
              style={{ color: "#ffb4b4", fontSize: ".9rem", marginTop: 4, gridColumn: "1 / -1" }}
            >
              {errorMsg}
            </div>
          )}
        </div>

        {/* Listado agrupado por compañía (ya filtrado por la compañía actual) */}
        <div className="b81-listado" style={{ marginTop: 10 }}>
          {Object.keys(grouped).length === 0 ? (
            <div
              className="b81-empty"
              style={{
                opacity: 0.8,
                padding: 12,
                background: "#0e1319",
                border: "1px dashed #2a3442",
                borderRadius: 10,
              }}
            >
              No hay bomberos 0-8 actualmente.
            </div>
          ) : (
            Object.entries(grouped).map(([cia, items]) => (
              <div key={cia} className="b81-group" style={{ marginTop: 12 }}>
                <h3 style={{ margin: "10px 0", fontSize: "1rem", fontWeight: 700 }}>{cia}</h3>
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {items.map((b) => {
                    const ini = toDateFlexible(b.fecha_inicio);
                    const fin = toDateFlexible(b.fecha_fin);
                    const rango =
                      ini && fin ? `${ini.toLocaleString()} → ${fin.toLocaleString()}` : "—";
                    return (
                      <li
                        key={b.id}
                        className="b81-item"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: 10,
                          border: "1px solid #2a3442",
                          borderRadius: 12,
                          background: "#0e1319",
                          marginBottom: 8,
                        }}
                      >
                        <div
                          className="b81-left"
                          style={{ display: "flex", gap: 10, alignItems: "center" }}
                        >
                          {b.fotoURL ? (
                            <img
                              className="b81-foto"
                              src={b.fotoURL}
                              alt={b.nombre}
                              style={{
                                width: 52,
                                height: 69,
                                objectFit: "cover",
                                borderRadius: 6,
                                background: "#1a222c",
                              }}
                            />
                          ) : (
                            <div
                              className="b81-foto placeholder"
                              style={{
                                width: 52,
                                height: 69,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "#8da2b5",
                                fontSize: ".72rem",
                                borderRadius: 6,
                                background: "#1a222c",
                              }}
                            >
                              SIN FOTO
                            </div>
                          )}

                          <div className="b81-info" style={{ display: "flex", flexDirection: "column" }}>
                            <div className="b81-nombre" style={{ fontWeight: 700 }}>
                              {b.nombre}
                            </div>
                            <div className="b81-sub" style={{ fontSize: ".85rem", opacity: 0.85 }}>
                              {b.cargo || ""}
                            </div>
                            <div
                              className="b81-rango"
                              style={{ fontSize: ".85rem", opacity: 0.85, marginTop: 2 }}
                            >
                              {rango}
                            </div>
                            {b.motivo ? (
                              <div
                                className="b81-motivo"
                                style={{ fontSize: ".85rem", opacity: 0.9, marginTop: 2 }}
                              >
                                Motivo: {b.motivo}
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <div className="b81-right">
                          <button
                            className="b81-salir"
                            onClick={() => handleSalirConfirm(b)}
                            style={{
                              background: "#ef5350",
                              border: "none",
                              color: "#121820",
                              fontWeight: 700,
                              padding: "8px 12px",
                              borderRadius: 10,
                              cursor: "pointer",
                            }}
                          >
                            Salir
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
